import React, { FC, useMemo, useCallback } from 'react'
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd'
import { FormProvider, useFieldArray, useWatch } from 'react-hook-form'
import classNames from 'classnames'
import { ISelectOption } from 'components/forms/Selects'
import compact from 'lodash/compact'
import get from 'lodash/get'
import ControlledCheckbox from 'transfix-form/components/ControlledCheckbox'
import ControlledSelect, {
  DeprecatedControlledSelect,
} from 'transfix-form/components/ControlledSelect'
import ControlledTextArea from 'transfix-form/components/ControlledTextArea'
import ControlledTextInput from 'transfix-form/components/ControlledTextInput'
import { FormModal, IFormModalProps, IModalAction, Button } from 'transfix-ui/components'
import { useGlobalToast } from 'transfix-ui/components/Toast/GlobalToast'
import { Box, Grid, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import {
  ShipmentIssueType,
  useCreateShipmentIssueType,
  useUpdateShipmentIssueType,
} from 'apps/marketplace/apiTypes/shipmentIssues'
import { AddIcon } from 'transfix-icons'
import { useIssuesContext } from '../IssuesContext'
import { IIssueAutomationRuleFragment } from '../automation/graphql/IssueAutomationRuleFragment'
import { useIssueAutomationRulesByIssueTypeQuery } from '../automation/graphql/IssueAutomationRulesByIssueType'
import { useIssuePriorityOptions } from '../shared/priorityOptions'
import { ListStepItem } from './ListStepItem'
import AutomatedRulesSection from './components/AutomatedRulesSection'
import ControlledSlaOptions from './components/ControlledSlaOptions'
import Priorities from './components/Priorities'
import Roles from './components/Roles'
import useAssigneeRoles from './hooks/useAssigneeRoles'
import useIssueTypeForm from './hooks/useIssueTypeForm'
import { getDefaultValuesFromIssue, ISSUE_TYPE_DEFAULTS } from './utils/getDefaultValuesFromIssue'

const useIssueTypeModalClasses = makeStyles(theme => ({
  header: {
    borderBottom: `1px solid ${brandColors.coolGray4}`,
  },
  listDraggable: {
    listStyleType: 'none',
  },
  iconAction: {
    transform: `scale(1.3)`,
    cursor: 'pointer',
  },
  buttonAdd: {
    marginLeft: '55px',
    marginTop: '20px',
  },
  addIcon: {
    marginRight: '5px',
  },
  section: {
    paddingBottom: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
    '&:not(:last-of-type)': {
      borderBottom: `1px solid ${brandColors.coolGray4}`,
    },
  },
}))

export enum MODAL_TYPE {
  new = 'new',
  edit = 'edit',
}

interface IControlPanelIssueTypeModalProps extends Pick<IFormModalProps, 'onClose' | 'open'> {
  id: string | number | null
  type: keyof typeof MODAL_TYPE
  /**
   * Issue Type for pre populating the fields in the form when editing
   * For the creation modal will be the default value
   * @default {}
   */
  issueType?: ShipmentIssueType
}

const TITLE_MAP = {
  new: 'Create New Issue Type',
  edit: 'Edit Issue Type',
}

const ControlPanelIssueTypeModal: FC<IControlPanelIssueTypeModalProps> = ({
  issueType,
  onClose,
  open,
  type,
}) => {
  const { openToast } = useGlobalToast()
  const { options, refetchIssueTypes } = useIssuesContext()
  const title = type && TITLE_MAP[type]
  const classes = useIssueTypeModalClasses()

  const { data: rulesData } = useIssueAutomationRulesByIssueTypeQuery({
    variables: {
      shipmentIssueTypeId: issueType?.id.toString() || '',
    },
    skip: !issueType,
  })

  const defaultPriorityOptions = useIssuePriorityOptions()

  const isMpStepsToResolveEnabled = useFeatureFlag('mpStepsToResolve')
  const issueSLAsAndEscalationEnabled = useFeatureFlag('issueSLAsAndEscalation')
  const isMpShipmentIssueAutomationEnabled = useFeatureFlag('mpShipmentIssueAutomation')
  const defaultValues = useMemo(
    () => ({
      ...ISSUE_TYPE_DEFAULTS,
      ...getDefaultValuesFromIssue(issueType),
    }),
    [issueType]
  )

  const methods = useIssueTypeForm({
    defaultValues,
  })
  const { control, errors, handleSubmit } = methods

  const values = useWatch({
    control,
    name: [
      'shipmentIssueCategoryId',
      'name',
      'description',
      'shipmentIssueTypeDefaultOwner',
      'shipmentIssueTypeDefaultSubscribers',
      'stepsToResolve',
      'issueResolutionSteps',
    ],
    defaultValue: defaultValues,
  })

  const stepsField = isMpStepsToResolveEnabled ? 'issueResolutionSteps' : 'stepsToResolve'
  const { append, fields, move, remove } = useFieldArray({
    control,
    name: stepsField,
  })

  const dragHandler = ({ destination, source }: DropResult) => {
    if (destination) {
      move(source.index, destination.index)
    }
  }

  const deleteStepHandler = useCallback(
    (index: number) => {
      remove(index)
    },
    [remove]
  )

  const addStepHandler = () => append({ name: '' })

  const requiredFieldsFilledOut =
    !!values.name && !!values.description && !!values.shipmentIssueCategoryId

  const useAssigneeRolesRes = useAssigneeRoles(
    values.shipmentIssueTypeDefaultOwner,
    defaultValues.otherAssigneesList
  )
  const { assigneeRoles, assigneeRolesError, selectedOwnerObject } = useAssigneeRolesRes

  const {
    error: createError,
    loading: createLoading,
    mutate: createIssueType,
  } = useCreateShipmentIssueType({})
  const {
    error: updateError,
    loading: updateLoading,
    mutate: updateIssueType,
  } = useUpdateShipmentIssueType({
    id: issueType?.id || -1,
  })

  const isLoading = createLoading || updateLoading

  const modalErrors = useMemo(() => {
    return compact([createError?.message || updateError?.message, assigneeRolesError?.message])
  }, [createError?.message, updateError?.message, assigneeRolesError])

  const actionHandlerMap = useMemo(
    () => ({
      new: createIssueType,
      edit: updateIssueType,
    }),
    [createIssueType, updateIssueType]
  )

  const handleCreateIssueType = useCallback(
    async data => {
      if (isLoading) {
        return
      }

      const actionToPerform = actionHandlerMap[type]
      const completeMessage = type === 'new' ? 'Issue Type Created' : 'Issue Type Updated'

      const owner = selectedOwnerObject

      const subscriberRoles = assigneeRoles || []

      const shipmentIssueTypeDefaultSubscribers = [
        ...data.shipmentIssueTypeDefaultSubscribers,
        ...subscriberRoles.filter(({ id, type: assigneeType }) =>
          Boolean(data[`${assigneeType}-${id}`])
        ),
      ].map(sub => ({
        id: sub.id,
        type: sub.type || sub.groupName,
      }))

      await actionToPerform({
        ...data,
        ...(isMpStepsToResolveEnabled
          ? {
              stepsToResolve: data?.issueResolutionSteps?.map(
                (item: { name: string }) => item.name
              ),
              issueResolutionSteps: data?.issueResolutionSteps?.map(
                (item: { name: string }, index: number) => ({
                  id: get(item, 'itemId'),
                  description: get(item, 'name'),
                  stepSequence: index + 1,
                })
              ),
            }
          : {
              stepsToResolve: data?.stepsToResolve?.map((item: { name: string }) => item.name),
            }),
        shipmentIssueTypeDefaultOwner: owner || null,
        shipmentIssueTypeDefaultSubscribers,
      })
      refetchIssueTypes()
      openToast({
        toastMessage: completeMessage,
        toastType: 'default',
      })
      onClose()
    },
    [
      isLoading,
      actionHandlerMap,
      type,
      selectedOwnerObject,
      assigneeRoles,
      refetchIssueTypes,
      onClose,
      isMpStepsToResolveEnabled,
    ]
  )

  const actions = useMemo<IModalAction[]>(
    () => [
      {
        label: 'Cancel',
        type: 'dismiss',
        dataTest: `${type}-issue-type-modal-dismiss-button`,
        action: () => {
          onClose()
        },
      },
      {
        label: 'Save',
        type: 'confirm',
        dataTest: `${type}-issue-type-modal-submit-button`,
        action: handleSubmit(handleCreateIssueType),
        disabled: !requiredFieldsFilledOut,
      },
    ],
    [handleCreateIssueType, handleSubmit, requiredFieldsFilledOut, onClose, type]
  )

  const rules: IIssueAutomationRuleFragment[] =
    compact(rulesData?.issueAutomationRulesByIssueType.nodes) || []

  return (
    <FormProvider {...methods}>
      <FormModal
        isClose
        open={open}
        onClose={onClose}
        title={title}
        titleSize={4}
        headerClassName={classes.header}
        actions={actions}
        error={modalErrors}
        dataTest={`${type}-issue-type-modal`}>
        <Box mt={2} data-dd-action-name='Control Panel Issue Type Modal'>
          <Box display='flex' flexDirection='column' gap={1} className={classes.section}>
            <Grid container spacing={1}>
              <Grid item xs={7}>
                <ControlledSelect
                  control={control}
                  name='shipmentIssueCategoryId'
                  defaultValue=''
                  SelectProps={{
                    label: 'Issue Category',
                    dataTest: 'select-category',
                    options: options.categories,
                    placeholder: 'Select a Category',
                    error: !!errors.shipmentIssueCategoryId,
                    validationMessage: errors.shipmentIssueCategoryId?.message,
                    required: true,
                  }}
                />
              </Grid>
              <Grid item xs={11}>
                <ControlledTextInput
                  control={control}
                  name='name'
                  TextInputProps={{
                    name: 'name',
                    label: 'Issue Name',
                    placeholder: 'Enter an Issue Name',
                    dataTest: 'name',
                    error: !!errors.name,
                    validationMessage: errors.name?.message,
                    required: true,
                  }}
                />
              </Grid>
            </Grid>
            {issueSLAsAndEscalationEnabled && <ControlledSlaOptions />}
            {!isMpShipmentIssueAutomationEnabled && (
              <Grid>
                <ControlledCheckbox
                  control={control}
                  name='autoResolve'
                  CheckboxProps={{
                    label:
                      'Automatically resolve this issue type when shipment is marked delivered',
                  }}
                />
              </Grid>
            )}
          </Box>

          <div className={classes.section}>
            <Roles {...useAssigneeRolesRes} />
          </div>

          <div className={classes.section}>
            {issueSLAsAndEscalationEnabled ? (
              <Priorities />
            ) : (
              <Box width='30%'>
                <Typography variant='body1'>Default Priority</Typography>
                <DeprecatedControlledSelect
                  control={control}
                  name='defaultPriority'
                  SelectProps={{
                    large: true,
                    options: defaultPriorityOptions as ISelectOption[],
                    dataTest: 'issue-priority',
                  }}
                />
              </Box>
            )}
          </div>

          {isMpShipmentIssueAutomationEnabled && type === 'edit' && issueType && (
            <div className={classes.section}>
              <AutomatedRulesSection id={issueType.id} rules={rules} />
            </div>
          )}

          <div className={classes.section}>
            <ControlledTextArea
              control={control}
              name='description'
              TextAreaProps={{
                label: 'Issue Description',
                placeholder: 'e.g. When should this Issue be logged?',
                dataTest: 'description',
                required: true,
                size: 'medium',
              }}
            />

            <Box mt={1.5} maxWidth={500} data-dd-action-name='Edit Issue Type Steps To Resolve'>
              <Typography variant='body1'>Steps to Resolve</Typography>
              <Typography variant='caption' style={{ fontWeight: 'normal' }}>
                Outline the steps that a user should take in order to resolve this issue.
              </Typography>
              <DragDropContext onDragEnd={dragHandler}>
                <ul className={classes.listDraggable}>
                  <Droppable droppableId='test-items'>
                    {provided => (
                      <div {...provided.droppableProps} ref={provided.innerRef}>
                        {fields.map((item, index) => {
                          return (
                            <Draggable
                              // eslint-disable-next-line react/no-array-index-key
                              key={`${stepsField}[${index}]`}
                              draggableId={`item-${index}`}
                              index={index}>
                              {provided => (
                                <ListStepItem
                                  item={item}
                                  provided={provided}
                                  index={index}
                                  control={control}
                                  onClick={deleteStepHandler}
                                  parentName={stepsField}
                                  error={get(errors, `${stepsField}.${index}.name.message`)}
                                />
                              )}
                            </Draggable>
                          )
                        })}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </ul>
              </DragDropContext>
              <Button color='tertiary' className={classes.buttonAdd} onClick={addStepHandler}>
                <AddIcon className={classNames(classes.iconAction, classes.addIcon)} /> Add a step
              </Button>
            </Box>
          </div>
        </Box>
      </FormModal>
    </FormProvider>
  )
}

export default ControlPanelIssueTypeModal
